/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

const initSwiper = () => {
    if (typeof Swiper === 'undefined') return;
    const swiperContainers = document.querySelectorAll('.product-item .color_variantion_images.swiper:not(.swiper-initialized)');
    swiperContainers.forEach(container => {
        new Swiper(container, {
            slidesPerView: 4.6,
            spaceBetween: 8,
            observer: true,
            observeParents: true,
            nested: true,
            navigation: {
                nextEl: container.querySelector('.variant-swiper-button-next'),
                prevEl: container.querySelector('.variant-swiper-button-prev'),
            },
            watchOverflow: true,
            on: {
                init: function () {
                    this.update();
                }
            },
             breakpoints: {
                768: {
                    slidesPerView: 4.6
                }
            }
        });
    });
};

const initBannerSplitSliders = () => {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.bss-swiper:not(.swiper-initialized)').forEach(el => {
        const desktop = parseFloat(el.dataset.slidesDesktop) || 1;
        const mobile = parseFloat(el.dataset.slidesMobile) || 1;
        const speed = parseInt(el.dataset.speed) || 500;
        const sectionIdMatch = el.className.match(/bss-swiper--([^\s]+)/);
        const sectionId = sectionIdMatch ? sectionIdMatch[1] : null;
        const paginationEl = sectionId ? document.querySelector('.bss-pagination--' + sectionId) : null;

        new Swiper(el, {
            slidesPerView: mobile,
            spaceBetween: 16,
            speed: speed,
            loop: false,
            pagination: paginationEl ? {
                el: paginationEl,
                clickable: true,
            } : false,
            scrollbar: {
                el: el.querySelector('.bss-swiper__scrollbar'),
                draggable: true,
            },
            navigation: {
                nextEl: el.querySelector('.swiper-button-next'),
                prevEl: el.querySelector('.swiper-button-prev'),
            },
            breakpoints: {
                768: {
                    slidesPerView: desktop,
                    spaceBetween: 24,
                }
            }
        });
    });
};

const updateNotSlidingState = (swiper) => {
    if (!swiper || !swiper.el) return;
    const isNotSliding = Boolean(swiper.isLocked || (swiper.snapGrid && swiper.snapGrid.length <= 1));
    swiper.el.classList.toggle('is-not-sliding', isNotSliding);
    swiper.el.classList.toggle('not-sliding', isNotSliding);
    swiper.el.classList.toggle('swiper-not-sliding', isNotSliding);
    swiper.el.classList.toggle('bs-slider--not-sliding', isNotSliding);
};

const initBannerSliders = () => {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.bs-slider:not(.swiper-initialized)').forEach(el => {
        let desktop = parseFloat(el.dataset.slidesDesktop) || 2;
        let tablet = el.dataset.slidesTablet ? parseFloat(el.dataset.slidesTablet) : desktop;
        let mobile = parseFloat(el.dataset.slidesMobile) || 1;
        let rowsDesktop = parseInt(el.dataset.rowsDesktop) || 1;
        let rowsMobile = parseInt(el.dataset.rowsMobile) || 1;
        let gapDesktop = parseInt(el.dataset.gapDesktop) || 20;
        let gapMobile = parseInt(el.dataset.gapMobile) || 8;
        const autoplay = el.dataset.autoplay === 'true';
        const speed = parseInt(el.dataset.speed) || 500;
        const isPeek = el.dataset.peek === 'true';

        let swiperOptions = {
            slidesPerView: mobile,
            grid: {
                rows: rowsMobile,
                fill: 'row'
            },
            spaceBetween: gapMobile,
            speed: speed,
            loop: false,
            watchOverflow: true,
            autoplay: autoplay ? { delay: 5000, disableOnInteraction: false } : false,
            pagination: {
                el: el.querySelector('.swiper-pagination'),
                clickable: true,
            },
            scrollbar: {
                el: el.querySelector('.swiper-scrollbar'),
                draggable: true,
            },
            navigation: {
                nextEl: el.querySelector('.swiper-button-next'),
                prevEl: el.querySelector('.swiper-button-prev'),
            },
            breakpoints: {
                768: {
                    slidesPerView: tablet,
                    grid: {
                        rows: rowsDesktop,
                        fill: 'row'
                    },
                    spaceBetween: gapDesktop,
                },
                1000: {
                    slidesPerView: desktop,
                    grid: {
                        rows: rowsDesktop,
                        fill: 'row'
                    },
                    spaceBetween: gapDesktop,
                }
            },
            on: {
                init: function () {
                    updateNotSlidingState(this);
                },
                lock: function () {
                    updateNotSlidingState(this);
                },
                unlock: function () {
                    updateNotSlidingState(this);
                },
                resize: function () {
                    updateNotSlidingState(this);
                },
                breakpoint: function () {
                    updateNotSlidingState(this);
                },
                update: function () {
                    updateNotSlidingState(this);
                }
            }
        };

        if (isPeek) {
            // Partial reveal is handled by overflow:visible and container alignment
        }

        const swiper = new Swiper(el, swiperOptions);
        updateNotSlidingState(swiper);
    });
};

const initRankingSwipers = () => {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.rk-swiper:not(.swiper-initialized)').forEach(el => {
        const desktop = parseFloat(el.dataset.slidesDesktop) || 5;
        const tablet = parseFloat(el.dataset.slidesTablet) || 3.5;
        const mobile = parseFloat(el.dataset.slidesMobile) || 1;

        new Swiper(el, {
            slidesPerView: mobile,
            spaceBetween: 12,
            pagination: {
                el: el.querySelector('.swiper-pagination'),
                clickable: true,
            },
            scrollbar: {
                el: el.querySelector('.swiper-scrollbar'),
                draggable: true,
            },
            navigation: {
                nextEl: el.querySelector('.swiper-button-next'),
                prevEl: el.querySelector('.swiper-button-prev'),
            },
            breakpoints: {
                768: {
                    slidesPerView: tablet,
                    spaceBetween: 16,
                },
                1000: {
                    slidesPerView: desktop,
                    spaceBetween: 16,
                }
            }
        });
    });
};

const initProductVideoHover = () => {
    if (window.productVideoHoverInitialized) return;
    window.productVideoHoverInitialized = true;

    // Use event delegation on document level to support dynamically loaded content
    document.addEventListener('mouseover', (event) => {
        const item = event.target.closest('.product-item__image-wrapper');
        if (!item) return;

        // Prevent restarting/interruption when moving between children of the same product-item
        if (event.relatedTarget && item.contains(event.relatedTarget)) {
            return;
        }

        const video = item.querySelector('.product-item__video');
        if (video) {
            video.play().catch(e => console.log('Video play failed:', e));
        }
    });

    document.addEventListener('mouseout', (event) => {
        const item = event.target.closest('product-item');
        if (!item) return;

        // Prevent pausing when moving between children of the same product-item
        if (event.relatedTarget && item.contains(event.relatedTarget)) {
            return;
        }

        const video = item.querySelector('.product-item__video');
        if (video) {
            video.pause();
            video.currentTime = 0;
        }
    });
};

const initAllSliders = () => {
    initSwiper();
    initBannerSplitSliders();
    initBannerSliders();
    initRankingSwipers();
    initProductVideoHover();
};


document.addEventListener('DOMContentLoaded', () => {
    initAllSliders();
    alignProductItemHeights();

    // Handle click on color swatches
    document.body.addEventListener('click', (e) => {
        const swatch = e.target.closest('.color-image-link');
        if (swatch) {
            const productItem = swatch.closest('product-item');
            if (!productItem) return;

            const newImgUrl = swatch.getAttribute('data-img-link');
            const newVariantUrl = swatch.getAttribute('data-variant-url');

            const primaryImg = productItem.querySelector('.product-item__primary-image');
            if (primaryImg) {
                primaryImg.src = newImgUrl;
                primaryImg.srcset = '';
            }

            const productLinks = productItem.querySelectorAll('a[href*="/products/"]');
            productLinks.forEach(link => {
                link.href = newVariantUrl;
            });

            const newTitle = swatch.getAttribute('data-title');
            if (newTitle) {
                const titleEl = productItem.querySelector('.product-item-meta__title');
                if (titleEl) {
                    titleEl.innerHTML = newTitle.trim();
                }
            }

            const newLabels = swatch.getAttribute('data-labels');
            if (newLabels !== null) {
                const labelsEl = productItem.querySelector('.product-item__label-list_top');
                if (labelsEl) {
                    labelsEl.innerHTML = newLabels.trim();
                }
            }

            productItem.querySelectorAll('.color-image-link').forEach(item => item.classList.remove('active'));
            swatch.classList.add('active');

            e.preventDefault();
            e.stopPropagation();
        }
    });

    // Ranking Tabs
    document.body.addEventListener('click', (e) => {
        const tab = e.target.closest('.rk-tabs__btn');
        if (!tab) return;

        const panelId = tab.dataset.rkTab;
        const container = tab.closest('.section__color-wrapper');

        container.querySelectorAll('.rk-tabs__btn').forEach(t => t.classList.remove('rk-tabs__btn--active'));
        tab.classList.add('rk-tabs__btn--active');

        container.querySelectorAll('.rk-collection').forEach(p => {
            if (p.dataset.rkPanel === panelId) {
                p.classList.remove('rk-collection--hidden');
                const swiper = p.querySelector('.rk-swiper');
                if (swiper && swiper.swiper) {
                    swiper.swiper.update();
                }
            } else {
                p.classList.add('rk-collection--hidden');
            }
        });
    });

    // Product Tab logic
    document.body.addEventListener('click', (e) => {
        const titleItem = e.target.closest('.product-form__tab_group .title_item');
        if (!titleItem) return;

        const tabGroup = titleItem.closest('.product-form__tab_group');
        const titles = Array.from(tabGroup.querySelectorAll('.title_item'));
        const contents = Array.from(tabGroup.querySelectorAll('.tab_content'));
        const index = titles.indexOf(titleItem);

        titles.forEach(t => t.classList.remove('active'));
        contents.forEach(c => c.classList.remove('active'));

        titleItem.classList.add('active');
        if (contents[index]) {
            contents[index].classList.add('active');
        }
    });
/*
    // Back In Stock logic
    if (!window.bisListenerBound) {
        window.bisListenerBound = true;
        document.body.addEventListener('click', (e) => {
            const bisBtn = e.target.closest('.backInStockBtn');
            if (!bisBtn) return;

            e.preventDefault();
            e.stopImmediatePropagation();
            
            const variantId = bisBtn.getAttribute('data-variant-id');

            if (typeof window.BISPopover !== 'undefined') {
                if (variantId) {
                    window.BISPopover.show({ variantId: variantId });
                } else {
                    window.BISPopover.show();
                }
            } else {
                const bisTrigger = document.getElementById('BIS_trigger');
                if (bisTrigger) {
                    bisTrigger.click();
                } else {
                    const bisFrame = document.getElementById('BIS_frame');
                    if (bisFrame) {
                        bisFrame.style.display = 'block';
                    } else {
                        alert("Back in Stock popup is not loaded on this page. Please enable 'Collection Pages' support in the Back In Stock app settings.");
                    }
                }
            }
        });
    }
*/
    // Automatically observe DOM mutations to initialize Swipers for dynamically loaded components (e.g. recommended products)
    let mutationTimeout;
    const observer = new MutationObserver((mutations) => {
        let shouldInit = false;
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    if (node.tagName === 'PRODUCT-ITEM' || node.querySelector('product-item')) {
                        shouldInit = true;
                        break;
                    }
                }
            }
            if (shouldInit) break;
        }
        if (shouldInit) {
            clearTimeout(mutationTimeout);
            mutationTimeout = setTimeout(() => {
                initSwiper();
                alignProductItemHeights();
            }, 100);
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('theme:loading:end', () => {
        initAllSliders();
        alignProductItemHeights();
    });
});

function alignProductItemHeights() {
    document.querySelectorAll('.product-list__inner').forEach(list => {
        const items = Array.from(list.querySelectorAll('product-item, .product-item'));
        if (items.length === 0) return;

        // Reset min-heights first to get natural heights
        items.forEach(item => {
            const title = item.querySelector('.product-item-meta__title');
            const labels = item.querySelector('.product-item__label-list');
            if (title) title.style.minHeight = '';
            if (labels) labels.style.minHeight = '';
        });

        // Group by row using offsetTop
        const rows = {};
        items.forEach(item => {
            const top = item.offsetTop;
            if (!rows[top]) rows[top] = [];
            rows[top].push(item);
        });

        // Set max heights per row
        Object.values(rows).forEach(row => {
            let maxTitleHeight = 0;
            let maxLabelsHeight = 0;

            row.forEach(item => {
                const title = item.querySelector('.product-item-meta__title');
                const labels = item.querySelector('.product-item__label-list');
                
                if (title) maxTitleHeight = Math.max(maxTitleHeight, title.offsetHeight);
                if (labels) maxLabelsHeight = Math.max(maxLabelsHeight, labels.offsetHeight);
            });

            row.forEach(item => {
                const title = item.querySelector('.product-item-meta__title');
                const labels = item.querySelector('.product-item__label-list');
                
                if (title && maxTitleHeight > 0) title.style.minHeight = `${maxTitleHeight}px`;
                if (labels && maxLabelsHeight > 0) labels.style.minHeight = `${maxLabelsHeight}px`;
            });
        });
    });
}

window.addEventListener('resize', () => {
    // Debounce resize
    clearTimeout(window.alignProductItemsTimeout);
    window.alignProductItemsTimeout = setTimeout(alignProductItemHeights, 150);
});

// Shopify Theme Editor Events
document.addEventListener('shopify:section:load', (event) => {
    initAllSliders();
});
document.addEventListener('shopify:section:select', (event) => {
    // Optional: add any logic for when a section is selected
});

/*------------------------------------------------------------------------------------------------------------
 * WISHLIST PLUS (SWYM) INTEGRATION
 * ----------------------------------------------------------------------------------------------------------*/
(function () {
    window.SwymCallbacks = window.SwymCallbacks || [];

    let wishlistedVariantIds = new Set();
    let wishlistedProductIds = new Set();
    let currentSelectedVariant = null;
    let lastToggleTime = 0;
    let lastToggledVariantId = null;
    let lastToggledAction = null; // 'add' or 'remove'
    let isToggling = false;
    let fetchDebounceTimer = null;
    let variantUpdateTimer = null;

    function syncAllWishlistButtons() {
        document.querySelectorAll('.swym-add-to-wishlist, [data-swaction="addToWishlist"], .mt-addToWishlist').forEach(el => {
            const wrapper = el.classList.contains('mt-addToWishlist') ? el : el.closest('.mt-addToWishlist');
            const btn = el.classList.contains('swym-add-to-wishlist') ? el : (wrapper ? wrapper.querySelector('.swym-add-to-wishlist, [data-swaction="addToWishlist"]') : null);

            const variantId = getLiveVariantId(wrapper, btn);
            const productId = String((btn && btn.dataset.productId) || (wrapper && wrapper.dataset.productId) || '');

            const isWishlisted = variantId ? wishlistedVariantIds.has(String(variantId)) : (productId && wishlistedProductIds.has(String(productId)));

            if (isWishlisted) {
                if (btn) btn.classList.add('swym-added', 'added-to-wishlist');
                if (wrapper) wrapper.classList.add('added-to-wishlist');
            } else {
                if (btn) btn.classList.remove('swym-added', 'added-to-wishlist');
                if (wrapper) wrapper.classList.remove('added-to-wishlist');
            }
        });
    }

    function updateSwymWishlistButtons(swat) {
        if (!swat) return;
        clearTimeout(fetchDebounceTimer);
        fetchDebounceTimer = setTimeout(function () {
            swat.fetch(function (products) {
                const fetchedVariantIds = new Set(
                    (products || []).map(p => String(p.epi || p.du || p.id || ''))
                );
                const fetchedProductIds = new Set(
                    (products || []).map(p => String(p.empi || p.id || ''))
                );

                // Preserve optimistic state if a user action occurred within the last 2.5s
                if (Date.now() - lastToggleTime < 2500 && lastToggledVariantId) {
                    if (lastToggledAction === 'add') {
                        fetchedVariantIds.add(String(lastToggledVariantId));
                    } else if (lastToggledAction === 'remove') {
                        fetchedVariantIds.delete(String(lastToggledVariantId));
                    }
                }

                wishlistedVariantIds = fetchedVariantIds;
                wishlistedProductIds = fetchedProductIds;
                syncAllWishlistButtons();
            });
        }, 150);
    }

    window.SwymCallbacks.push(function (swat) {
        window._swat = swat;
        updateSwymWishlistButtons(swat);

        if (swat.evtLayer) {
            swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, function () {
                updateSwymWishlistButtons(swat);
            });
            swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, function () {
                updateSwymWishlistButtons(swat);
            });
        }
    });

    document.addEventListener("openable-element:load:end", function () {
        if (window._swat) {
            updateSwymWishlistButtons(window._swat);
        }
    }, true);

    function getLiveVariantId(wrapper, btn) {
        const form = (wrapper && wrapper.closest('form')) || (btn && btn.closest('form'));
        const idInput = form ? form.querySelector('input[name="id"]') : null;
        return String(
            (idInput && idInput.value) ||
            (currentSelectedVariant && currentSelectedVariant.id) ||
            (btn && (btn.dataset.variantId || btn.getAttribute('data-variant-id'))) ||
            (wrapper && (wrapper.dataset.variantId || wrapper.getAttribute('data-variant-id'))) ||
            ''
        );
    }

    function toggleWishlist(targetEl) {
        if (isToggling) return;
        isToggling = true;
        setTimeout(function () {
            isToggling = false;
        }, 300);

        const wrapper = targetEl.closest('.mt-addToWishlist');
        const btn = targetEl.closest('.swym-add-to-wishlist, [data-swaction="addToWishlist"]') || (wrapper ? wrapper.querySelector('.swym-add-to-wishlist, [data-swaction="addToWishlist"]') : null);

        if (!window._swat) {
            window.SwymCallbacks = window.SwymCallbacks || [];
            window.SwymCallbacks.push(function (swat) {
                window._swat = swat;
                toggleWishlist(targetEl);
            });
            return;
        }

        const variantId = getLiveVariantId(wrapper, btn);
        const productId = String((btn && btn.dataset.productId) || (wrapper && wrapper.dataset.productId) || '');
        const productUrl = (btn && btn.dataset.productUrl) || (variantId ? window.location.pathname + '?variant=' + variantId : window.location.href);

        if (!variantId && !productId) return;

        const isWishlisted = variantId
            ? wishlistedVariantIds.has(String(variantId))
            : (productId && wishlistedProductIds.has(String(productId)));

        const productData = {
            epi: Number(variantId) || variantId,
            empi: Number(productId) || productId,
            du: productUrl
        };

        lastToggleTime = Date.now();
        lastToggledVariantId = String(variantId || '');

        if (isWishlisted) {
            lastToggledAction = 'remove';
            if (variantId) wishlistedVariantIds.delete(String(variantId));
            syncAllWishlistButtons();
            window._swat.removeFromWishList(productData, function () {
                updateSwymWishlistButtons(window._swat);
            });
        } else {
            lastToggledAction = 'add';
            if (variantId) wishlistedVariantIds.add(String(variantId));
            syncAllWishlistButtons();
            window._swat.addToWishList(productData, function () {
                updateSwymWishlistButtons(window._swat);
            });
        }
    }

    function updateWishlistForVariant(variant, container) {
        if (!variant || !variant.id) return;
        currentSelectedVariant = variant;
        const root = container || document;

        const wrappers = root.querySelectorAll('.mt-addToWishlist');
        wrappers.forEach(w => {
            w.dataset.variantId = variant.id;
            w.setAttribute('data-variant-id', variant.id);
        });

        const wishlistBtns = root.querySelectorAll('.swym-add-to-wishlist, [data-swaction="addToWishlist"]');
        wishlistBtns.forEach(btn => {
            btn.dataset.variantId = variant.id;
            btn.setAttribute('data-variant-id', variant.id);
            if (variant.url) {
                const fullUrl = window.location.origin + variant.url;
                btn.dataset.productUrl = fullUrl;
                btn.setAttribute('data-product-url', fullUrl);
            }
        });

        syncAllWishlistButtons();
    }

    // Handle variant selection change in product form
    const handleVariantChange = function (e) {
        const variant = e.detail ? e.detail.variant : null;
        if (!variant || !variant.id) return;

        const container = (e.target && e.target.closest) ? (e.target.closest('form') || e.target) : document;

        updateWishlistForVariant(variant, container);

        clearTimeout(variantUpdateTimer);
        variantUpdateTimer = setTimeout(function () {
            updateWishlistForVariant(variant, container);
        }, 100);
    };

    document.addEventListener('variant:change', handleVariantChange);
    document.addEventListener('variant:changed', handleVariantChange);
    document.addEventListener('product:rerender', function () {
        if (currentSelectedVariant) {
            clearTimeout(variantUpdateTimer);
            variantUpdateTimer = setTimeout(function () {
                updateWishlistForVariant(currentSelectedVariant);
            }, 60);
        }
    });

    // Capture click on .mt-addToWishlist or Swym buttons to guarantee wishlist toggle for active variant on first click
    document.addEventListener('click', function (e) {
        const target = e.target.closest('.mt-addToWishlist, .swym-add-to-wishlist, [data-swaction="addToWishlist"]');
        if (!target) return;

        e.preventDefault();
        e.stopPropagation();
        toggleWishlist(target);
    }, true);
})();
/* Mobile Scrollable Menu JS */
(function () {
    function initMobileNav() {
        const parent = document.getElementById('mobile-custom-navigation');
        if (!parent) return;

        const links = parent.querySelectorAll('.scroll-nav__link');
        const container = parent.querySelector('#mobileSubmenuContainer');
        const contents = parent.querySelectorAll('.submenu-content');

        if (!links.length) return;

        function updateSubmenu(targetId) {
            let hasContent = false;
            contents.forEach(content => {
                if (content.id === targetId) {
                    content.classList.add('is-active');
                    hasContent = true;
                } else {
                    content.classList.remove('is-active');
                }
            });

            if (hasContent) {
                container.classList.add('is-visible');
            } else {
                container.classList.remove('is-visible');
            }
        }

        links.forEach(link => {
            link.addEventListener('click', function (e) {
                const hasSubmenu = this.getAttribute('data-has-submenu') === 'true';
                const targetId = this.getAttribute('data-target');
                const isCurrentlyActive = this.classList.contains('is-active');

                if (hasSubmenu) {
                    e.preventDefault();

                    if (isCurrentlyActive) {
                        // If already active, toggle it OFF
                        this.classList.remove('is-active');
                        container.classList.remove('is-visible');
                    } else {
                        // Otherwise, switch to the new one
                        links.forEach(l => l.classList.remove('is-active'));
                        this.classList.add('is-active');
                        updateSubmenu(targetId);
                    }
                } else {
                    // Links without submenus hide the container and proceed normally
                    links.forEach(l => l.classList.remove('is-active'));
                    container.classList.remove('is-visible');
                }
            });
        });

        // Initial state
        const activeLink = parent.querySelector('.scroll-nav__link.is-active');
        if (activeLink && activeLink.getAttribute('data-has-submenu') === 'true') {
            //updateSubmenu(activeLink.getAttribute('data-target'));
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initMobileNav);
    } else {
        initMobileNav();
    }

    // Add hover effect to buttons
    document.querySelectorAll('button, .button, [role="button"]').forEach(button => {
        button.addEventListener('mouseenter', () => {
            button.style.transform = 'translateY(-2px)';
        });

        button.addEventListener('mouseleave', () => {
            button.style.transform = 'translateY(0)';
        });
    });


    var parent = document.getElementById('mobile-custom-navigation');
    window.addEventListener('scroll', function () {
        if (window.scrollY > 100) {
            parent.classList.add('is-scrolled');
        } else {
            parent.classList.remove('is-scrolled');
        }
    });


})();

// FAQ Scroll Spy Custom Offset
document.addEventListener('click', (e) => {
    const anchor = e.target.closest('.scroll-spy__anchor');
    if (anchor) {
        const targetId = anchor.getAttribute('href');
        const targetElement = document.querySelector(targetId);

        if (targetElement) {
            e.preventDefault();
            const offset = 100; // Fixed offset as requested
            const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - offset;

            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
        }
    }
}, true);

// Quick Buy Header Color Selector
document.addEventListener('change', (e) => {
    if (e.target.classList.contains('qb-header-color-radio')) {
        const wrapper = e.target.closest('.quick-buy-product-main');
        if (!wrapper) return;

        // Update Image
        const imageEl = wrapper.querySelector('.quick-buy-product__image');
        const imageUrl = e.target.getAttribute('data-image-url');
        if (imageEl && imageUrl) {
            imageEl.src = imageUrl;
            imageEl.srcset = ''; // Clear srcset to force using the new src
        }

        // Update Price
        const priceContainer = wrapper.querySelector('.price-list');
        const priceHtml = e.target.getAttribute('data-price-html');
        if (priceContainer && priceHtml) {
            priceContainer.innerHTML = priceHtml;
        }

        // Update Color Name Label
        const colorNameEl = wrapper.querySelector('.qb-header-color-name');
        const colorName = e.target.getAttribute('data-color-name');
        if (colorNameEl && colorName) {
            colorNameEl.textContent = colorName;
        }

        // Scroll to the target group
        if (colorName) {
            const popupWrapper = e.target.closest('quick-buy-drawer') || e.target.closest('quick-buy-popover') || document;
            const group = popupWrapper.querySelector('.qb-color-group[data-color-group="' + colorName.replace(/"/g, '\\"') + '"]');
            if (group) {
                const container = group.closest('.drawer__content') || group.closest('.popover__content') || window;
                const containerTop = container === window ? 0 : container.getBoundingClientRect().top;
                const groupTop = group.getBoundingClientRect().top;
                const currentScroll = container === window ? window.pageYOffset : container.scrollTop;
                
                const stickyHeader = popupWrapper.querySelector('.quick-buy-product-main');
                const stickyOffset = (stickyHeader && window.innerWidth >= 750) ? stickyHeader.offsetHeight : 0;
                
                const offsetPosition = groupTop - containerTop + currentScroll - stickyOffset - 20;
                
                if (container === window) {
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                } else {
                    container.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
            }
        }
    }
});

/*------------------------------------------------------------------------------------------------------------
 * FAQ ACCORDION AUTO-OPEN ON URL HASH
 * ----------------------------------------------------------------------------------------------------------*/
(function () {
    function getCollapsibleTogglesForId(hashId) {
        if (!hashId) return { targetEl: null, toggles: [] };

        const decodedId = decodeURIComponent(hashId);
        const targetEl = document.getElementById(decodedId) || document.getElementById(hashId);
        if (!targetEl) return { targetEl: null, toggles: [] };

        const toggles = [];

        if (targetEl.classList.contains('faq__item')) {
            const toggle = targetEl.querySelector('.collapsible-toggle');
            if (toggle) toggles.push(toggle);
        } else {
            const childToggles = targetEl.querySelectorAll('.faq__item .collapsible-toggle, .collapsible-toggle');
            if (childToggles.length > 0) {
                childToggles.forEach(t => toggles.push(t));
            } else {
                const parentFaqItem = targetEl.closest('.faq__item');
                if (parentFaqItem) {
                    const toggle = parentFaqItem.querySelector('.collapsible-toggle');
                    if (toggle) toggles.push(toggle);
                }
            }
        }

        return { targetEl, toggles };
    }

    function openAccordionToggle(button) {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        const controlsId = button.getAttribute('aria-controls');
        const contentEl = controlsId ? document.getElementById(controlsId) : null;

        if (isExpanded || (contentEl && (contentEl.hasAttribute('open') || contentEl.open))) {
            return;
        }

        button.setAttribute('aria-expanded', 'true');
        if ('isExpanded' in button) {
            button.isExpanded = true;
        }

        if (contentEl) {
            contentEl.open = true;
            contentEl.setAttribute('open', '');
        }

        const faqItem = button.closest('.faq__item');
        if (faqItem && faqItem.dataset.category) {
            const categoryTarget = faqItem.dataset.category;
            const mobileTab = document.querySelector(`.faq-mobile-tabs__item[data-target="${categoryTarget}"]`);
            if (mobileTab && !mobileTab.classList.contains('is-active')) {
                mobileTab.click();
            }
        }
    }

    function handleUrlHashFaqOpen() {
        const hash = window.location.hash ? window.location.hash.substring(1) : '';
        if (!hash) return;

        const { toggles } = getCollapsibleTogglesForId(hash);
        if (toggles && toggles.length > 0) {
            openAccordionToggle(toggles[0]);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', handleUrlHashFaqOpen);
    } else {
        handleUrlHashFaqOpen();
    }

    window.addEventListener('load', handleUrlHashFaqOpen);
    window.addEventListener('hashchange', handleUrlHashFaqOpen);
    window.addEventListener('popstate', handleUrlHashFaqOpen);
})();


// ReviCo - variant change star restore
(function () {
  function initRevicoVariantRestore() {
    if (window.__revicoVariantRestoreInitialized) return;
    window.__revicoVariantRestoreInitialized = true;

    let revicoProductStarHtml = null;

    function cacheRevicoStar() {
      const target = document.querySelector(
        '.product-form__custom-liquid [data-revico-layout="goods-starsimple"]'
      );

      if (!target) return;

      const star = target.nextElementSibling;

        if (star && star.classList.contains('revico-star')) {
        const clone = star.cloneNode(true);

        clone.style.removeProperty('opacity');
        clone.style.removeProperty('display');

        revicoProductStarHtml = clone.outerHTML;
        }
    }

    const observer = new MutationObserver(function () {
      if (!revicoProductStarHtml) {
        cacheRevicoStar();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    cacheRevicoStar();

    // バリエーション変更
    document.addEventListener('variant:change', function () {
      setTimeout(function () {
        const target = document.querySelector(
          '.product-form__custom-liquid [data-revico-layout="goods-starsimple"]'
        );

        if (
          target &&
          !target.nextElementSibling?.classList.contains('revico-star') &&
          revicoProductStarHtml
        ) {
          target.insertAdjacentHTML(
            'afterend',
            revicoProductStarHtml
          );
        }
      }, 300);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initRevicoVariantRestore
    );
  } else {
    initRevicoVariantRestore();
  }
})();
